import React, { useEffect, useRef, useState } from "react";

import { Formio } from "@formio/react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { LuImageOff } from "react-icons/lu";
import { RxReload } from "react-icons/rx";
import { useParams, useNavigate } from "react-router-dom";

import { applyStableSvgVariableElementIds } from "../../common/CertificateOnboarding/utils/svgTemplatePreview";
import CfBreadcrumb from "../../common/CfBreadcrumb/CfBreadcrumb";
import CfLoader from "../../common/CfLoader/CfLoader";
import Dropdown from "../../common/Dropdown/Dropdown";
import {
  injectTemplatePreview,
  isHtmlTemplateSource,
  updateTemplatePreview,
} from "../../common/HtmlDesigner/preview/templatePreview";
import Toast from "../../common/Toast/Toast";
import SettingOption from "../../components/SettingOption/SettingOption";
import { imgRegex, whiteSpaceEmptyStrRegex } from "../../configs/App.Regex";
import EntityConstants from "../../configs/EntityConstants";
import {
  isHtmlTemplate,
  resolveTemplateSource,
} from "../../configs/templateTypes";
import { GET, POST } from "../../service/CRUDService";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import convertToBase64 from "../../utils/ConvertToBase64";
import { hasFormDataAttributes } from "../../utils/formUtils/hasFormDataAttributes";

const EditIssuerTemplate = () => {
  const navigate = useNavigate();
  const thumbnailImageInputRef = useRef(null);
  const formRef = useRef(null);
  const programmeDropdownRef = useRef(null);
  const batchDropdownRef = useRef(null);
  const { t } = useTranslation();

  const {
    credential_template_id,
    issuer_credential_template_id,
    template_type,
  } = useParams();
  const [certificateTitle, setCertificateTitle] = useState("");
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [error, setError] = useState("");
  const [programmeNameError, setProgrammeNameError] = useState("");
  const [batchNameError, setBatchNameError] = useState("");
  const [fieldsChanged, setFieldsChanged] = useState({});
  const [toggleMessage, setToggleMessage] = useState("");
  const [isInvalidCombination, setIsInvalidCombination] = useState(false);
  const [currentSVG, setCurrentSVG] = useState("");
  const [previewTemplateType, setPreviewTemplateType] = useState();
  const [certificateData, setCertificateData] = useState();
  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState({});
  const [certificateRedirectURL, setCertificateRedirectURL] = useState("");
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [isPreAuth, setIsPreAuth] = useState(false);
  const preAuthUserEditedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const [verificationCost, setVerificationCost] = useState("");
  const [isExpiryRequired, setIsExpiryRequired] = useState(false);
  const [expiryValue, setExpiryValue] = useState("");
  const [expiryUnit, setExpiryUnit] = useState("days");
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [programmeList, setProgrammeList] = useState([]);
  const [programmeNext, setProgrammeNext] = useState(null);
  const [programmeLoading, setProgrammeLoading] = useState(false);
  const [programmeDropdownOpen, setProgrammeDropdownOpen] = useState(false);
  const [programmeOffset, setProgrammeOffset] = useState(0);
  const [batchList, setBatchList] = useState([]);
  const [batchNext, setBatchNext] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);
  const [batchOffset, setBatchOffset] = useState(0);
  const [selectedProgrammeCode, setSelectedProgrammeCode] = useState("");
  const selectedProgrammeCodeRef = useRef(selectedProgrammeCode);
  const [orgLogo, setOrgLogo] = useState("");
  const [hasFormFields, setHasFormFields] = useState(false);
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const prefillSnapshotRef = useRef(null);
  const onSubmitRef = useRef(() => {});
  const [thumbnailImagePreview, setThumbnailImagePreview] = useState("");
  const [uploadedThumbnailImage, setUploadedThumbnailImage] = useState("");

  const breadcrumbItems = [
    {
      titleKey: "designs:edit",
      to: `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`,
    },
  ];

  const expiryUnitOptions = [
    { name: t("drafts:days"), value: "days" },
    { name: t("drafts:months"), value: "months" },
    { name: t("drafts:years"), value: "years" },
  ];

  const renderToContainer = (container, element) => {
    if (container) {
      const root = createRoot(container);
      root.render(element);
      return () => root.unmount();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const calculateExpiryDays = (value, unit) => {
    if (value === "" || value === null || value === undefined) return null;

    const numericValue = Number(value);
    if (!Number.isSafeInteger(numericValue) || numericValue <= 0) return null;

    if (unit === "months") return numericValue * 30;
    if (unit === "years") return numericValue * 365;
    return numericValue;
  };

  const getExpiryStateFromData = (data) => {
    const expiryConfig = data?.expiry_config;
    const isExpiryEnabled = data?.is_expires === true;

    if (isExpiryEnabled && expiryConfig) {
      return {
        isExpiryRequired: true,
        expiryValue:
          expiryConfig.expires_value !== undefined &&
          expiryConfig.expires_value !== null
            ? String(expiryConfig.expires_value)
            : expiryConfig.expires_in_days !== undefined &&
                expiryConfig.expires_in_days !== null
              ? String(expiryConfig.expires_in_days)
              : "",
        expiryUnit: expiryConfig.expires_unit || "days",
      };
    }

    if (isExpiryEnabled && data?.value && data?.unit) {
      return {
        isExpiryRequired: true,
        expiryValue: String(data.value),
        expiryUnit: data.unit,
      };
    }

    if (isExpiryEnabled && data?.expires_in_days) {
      return {
        isExpiryRequired: true,
        expiryValue: String(data.expires_in_days),
        expiryUnit: "days",
      };
    }

    return {
      isExpiryRequired: false,
      expiryValue: "",
      expiryUnit: "days",
    };
  };

  const handlePaymentCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    localStorage.setItem("verCheck", isChecked);
    setIsPaymentRequired(isChecked);

    if (!isChecked) {
      localStorage.removeItem("verCost");
      setVerificationCost("");
    }
  };

  const handleVerificationCostChange = (e) => {
    localStorage.setItem("verCheck", "true");
    localStorage.setItem("verCost", e.target.value);
    setVerificationCost(e.target.value);
  };

  const handleExpiryRequiredChange = (e) => {
    const isChecked = e.target.checked;
    setIsExpiryRequired(isChecked);

    if (!isChecked) {
      setExpiryValue("");
      setExpiryUnit("days");
    }
  };

  const handleExpiryValueChange = (e) => {
    setExpiryValue(e.target.value);
  };

  const handleExpiryUnitChange = (value) => {
    setExpiryUnit(value?.target ? value.target.value : value);
  };

  const handleThumbnailImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:upload_valid_image_error"),
      });
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:file_size_error_5mb"),
      });
      return;
    }

    try {
      const base64String = await convertToBase64(file);
      setThumbnailImagePreview(base64String);
      sessionStorage.setItem("thumbnail_image", base64String);
      Toast({
        type: "success",
        title: t("drafts:success"),
        message: t("drafts:thumbnail_uploaded_success"),
      });
    } catch (error) {
      console.error("Error converting image to base64:", error);
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:failed_upload_thumbnail"),
      });
    }
  };

  const handleClearUploadedThumbnail = () => {
    setThumbnailImagePreview("");
    setUploadedThumbnailImage("");
    sessionStorage.removeItem("thumbnail_image");
    if (thumbnailImageInputRef.current) {
      thumbnailImageInputRef.current.value = "";
    }
    Toast({
      type: "warning",
      title: t("drafts:warning"),
      message: t("drafts:thumbnail_removed"),
    });
  };

  const handleRemoveThumbnailImage = () => {
    handleClearUploadedThumbnail();
  };

  const fetchProgrammes = async (loadMore = false) => {
    try {
      setProgrammeLoading(true);
      const offset = loadMore ? programmeOffset : 0;
      const endpoint = `${EntityConstants.GET_PROGRAMME_DETAILS}?limit=10&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.data) {
        if (loadMore) {
          setProgrammeList((prev) => [...prev, ...data.data.results]);
        } else {
          setProgrammeList(data.data.results);
        }
        setProgrammeNext(data.data.next);
        setProgrammeOffset(offset + data.data.results.length);
      }
    } catch (error) {
      console.error("Error fetching programmes:", error);
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:failed_fetch_programmes"),
      });
    } finally {
      setProgrammeLoading(false);
    }
  };

  const handleProgrammeScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      programmeNext &&
      !programmeLoading
    ) {
      fetchProgrammes(true);
    }
  };

  const fetchBatches = async (loadMore = false) => {
    if (!selectedProgrammeCode) return;

    const requestedProgrammeCode = selectedProgrammeCode;
    try {
      setBatchLoading(true);
      const offset = loadMore ? batchOffset : 0;
      const endpoint = `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${requestedProgrammeCode}&limit=10&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (requestedProgrammeCode !== selectedProgrammeCodeRef.current) return;

      if (data.data) {
        if (loadMore) {
          setBatchList((prev) => [...prev, ...data.data.results]);
        } else {
          setBatchList(data.data.results);
        }
        setBatchNext(data.data.next);
        setBatchOffset(offset + data.data.results.length);
      }
    } catch (error) {
      if (requestedProgrammeCode === selectedProgrammeCodeRef.current) {
        Toast({
          type: "error",
          title: t("drafts:error"),
          message: t("drafts:failed_fetch_batches"),
        });
      }
    } finally {
      if (requestedProgrammeCode === selectedProgrammeCodeRef.current) {
        setBatchLoading(false);
      }
    }
  };

  const handleProgrammeSelect = (programme) => {
    const previousCode = localStorage.getItem("programme_code");
    setProgrammeName(programme.programme_name);
    selectedProgrammeCodeRef.current = programme.programme_code;
    setSelectedProgrammeCode(programme.programme_code);
    localStorage.setItem("programme_code", programme.programme_code);
    setBatchName("");
    localStorage.removeItem("batch_code");
    if (previousCode !== programme.programme_code) {
      setBatchList([]);
      setBatchNext(null);
      setBatchOffset(0);
    }
    setProgrammeDropdownOpen(false);
  };

  const handleBatchSelect = (batch) => {
    setBatchName(batch.batch_name);
    localStorage.setItem("batch_code", batch.batch_code);
    setBatchDropdownOpen(false);
  };

  const handleBatchScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      batchNext &&
      !batchLoading
    ) {
      fetchBatches(true);
    }
  };

  const getLogoData = (currentFields) => {
    if (currentFields?.logo?.[0]?.url) {
      return currentFields.logo;
    }
    if (orgLogo) {
      return [
        {
          storage: "base64",
          name: "org_logo.svg",
          url: orgLogo,
          type: "image/svg+xml",
        },
      ];
    }
    return undefined;
  };

  const validateRequiredFields = () => {
    const certTitle = sessionStorage.getItem("certificate_title");
    const progCode = localStorage.getItem("programme_code");
    const batchCode = localStorage.getItem("batch_code");
    const isEmpty = certTitle == null || certTitle === "";
    const isWhiteSpace = whiteSpaceEmptyStrRegex.test(certTitle);
    const isProgrammeNameEmpty =
      !progCode || progCode === "" || progCode === "";
    const isBatchNameEmpty = !batchCode || batchCode === "" || batchCode === "";
    const isVerificationAmountEmpty =
      localStorage.getItem("verCheck") === "true" &&
      (!localStorage.getItem("verCost") ||
        localStorage.getItem("verCost").trim() === "");

    if (isEmpty || isWhiteSpace || isProgrammeNameEmpty || isBatchNameEmpty) {
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:fill_required_fields"),
      });
      return false;
    }

    if (isVerificationAmountEmpty) {
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: t("drafts:verification_amount_required_payment"),
      });
      return false;
    }

    if (isExpiryRequired) {
      const expiryDays = calculateExpiryDays(expiryValue, expiryUnit);

      if (!expiryValue || expiryDays === null || expiryDays <= 0) {
        Toast({
          type: "error",
          title: t("drafts:error"),
          message: t("drafts:expiry_date_required_error"),
        });
        return false;
      }
    }

    return true;
  };

  const submitCertificateData = async (
    submissionData,
    isFormSubmission = false,
  ) => {
    if (!validateRequiredFields()) {
      return;
    }

    const logoData = getLogoData(
      isFormSubmission ? submissionData.data : fieldsChanged,
    );

    let verificationCostValue = null;
    if (localStorage.getItem("verCheck") === "true") {
      verificationCostValue = localStorage.getItem("verCost") * 100;
    } else if (certificateData?.verification_cost) {
      verificationCostValue = certificateData.verification_cost;
    }

    const payload = {
      form_data: isFormSubmission
        ? {
            ...submissionData,
            data: {
              ...submissionData.data,
              logo: logoData,
            },
          }
        : {
            data: {
              ...fieldsChanged,
              logo: logoData,
              submit: false,
            },
          },
      credential_template_id: credential_template_id,
      certificate_name: sessionStorage.getItem("certificate_title"),
      issuer_credential_template_id: issuer_credential_template_id,
      programme_code: localStorage.getItem("programme_code"),
      batch_code: localStorage.getItem("batch_code"),
      certificate_redirect_url:
        sessionStorage.getItem("certificate_redirect_url") || undefined,
      is_auth_required:
        sessionStorage.getItem("is_auth_required") !== null
          ? JSON.parse(sessionStorage.getItem("is_auth_required"))
          : undefined,
      is_public:
        sessionStorage.getItem("is_public") !== null
          ? JSON.parse(sessionStorage.getItem("is_public"))
          : undefined,
      is_pre_auth:
        sessionStorage.getItem("is_pre_auth") !== null
          ? JSON.parse(sessionStorage.getItem("is_pre_auth"))
          : undefined,
      is_payment_required: localStorage.getItem("verCheck") === "true",
      verification_cost: verificationCostValue,
      verification_fields: {
        components: selectedComponents.filter((comp) => comp.selected),
      },
    };

    payload.is_expires = isExpiryRequired;

    if (isExpiryRequired) {
      payload.is_expires = true;
      payload.expiry_config = {
        expires_in_days: calculateExpiryDays(expiryValue, expiryUnit),
        expires_value: Number(expiryValue),
        expires_unit: expiryUnit,
      };
    }

    const savedThumbnailImage = sessionStorage.getItem("thumbnail_image");
    if (savedThumbnailImage) {
      payload.thumbnail_image = savedThumbnailImage;
    } else if (uploadedThumbnailImage && !thumbnailImagePreview) {
      payload.thumbnail_image = null;
    } else if (
      uploadedThumbnailImage &&
      thumbnailImagePreview === uploadedThumbnailImage
    ) {
      payload.thumbnail_image = uploadedThumbnailImage;
    }

    if (payload.form_data.data.logo === undefined) {
      delete payload.form_data.data.logo;
    }

    try {
      setLoading(true);
      const isNewTemplateAddition =
        localStorage.getItem("is_new_template_addition") === "true";
      let actualIssuerCredentialTemplateId = issuer_credential_template_id;

      if (isNewTemplateAddition) {
        const clonePayload = {
          issuer_template_id: localStorage.getItem("issuer_template_id"),
          programme_code: localStorage.getItem("programme_code"),
          batch_code: localStorage.getItem("batch_code"),
        };

        const cloneResponse = await POST(
          `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/clone`,
          clonePayload,
        );

        if (!cloneResponse.ok) {
          const cloneError = await cloneResponse.json();
          Toast({
            type: "error",
            title: t("drafts:error"),
            message:
              cloneError.message || t("drafts:failed_add_template_batch"),
          });
          return;
        }

        const cloneData = await cloneResponse.json();
        actualIssuerCredentialTemplateId =
          cloneData.data?.issuer_credential_template_id ||
          cloneData.issuer_credential_template_id ||
          cloneData.data?.batch_template_id ||
          cloneData.batch_template_id;

        if (!actualIssuerCredentialTemplateId) {
          console.error(
            "Could not extract issuer_credential_template_id from clone response:",
            cloneData,
          );
          Toast({
            type: "error",
            title: t("drafts:error"),
            message: t("drafts:failed_get_template_id_clone"),
          });
          return;
        }

        payload.issuer_credential_template_id =
          actualIssuerCredentialTemplateId;
        sessionStorage.setItem("workspace_needs_refresh", "true");
      }

      const response = await POST(
        `${EntityConstants.POST_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
        payload,
      );

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem(
          "issuer_credential_template_id",
          data.issuer_credential_template_id,
        );
        if (template_type === "Legacy") {
          const safeCertificateName = encodeURIComponent(
            certificateData?.certificate_name ?? "",
          );
          navigate(
            `/issuer/workspace/batches/legacy-issuance/${data.credential_template_id}/${data.issuer_credential_template_id}/${safeCertificateName}/${template_type}`,
          );
        } else {
          navigate(
            `/issuer/certificate/issue/${data.credential_template_id}/${data.issuer_credential_template_id}/${localStorage.getItem("programme_code")}`,
          );
        }
        Toast({
          type: "success",
          title: t("drafts:success"),
          message: t("drafts:template_saved_success"),
        });
      } else {
        const errorData = await response.json();
        if (errorData.message && Array.isArray(errorData.message)) {
          errorData.message.forEach((msg) => {
            Toast({
              type: "error",
              title: t("drafts:error"),
              message: msg,
            });
          });
        } else {
          Toast({
            type: "error",
            title: t("drafts:error"),
            message: `${t("drafts:api_request_failed")}\n${t(
              "drafts:please_retry",
            )}`,
          });
        }
      }
    } catch (error) {
      console.error("An error occurred:", error);
      Toast({
        type: "error",
        title: t("drafts:error"),
        message: error.message || t("drafts:api_request_failed"),
      });
    } finally {
      setLoading(false);
      localStorage.removeItem("programme_code");
      localStorage.removeItem("programme_name");
      localStorage.removeItem("batch_code");
      localStorage.removeItem("batch_name");
      localStorage.removeItem("is_new_template_addition");
      localStorage.removeItem("issuer_template_id");
      sessionStorage.removeItem("thumbnail_image");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitCertificateData(null, false);
  };

  onSubmitRef.current = (sub) => submitCertificateData(sub, true);
  const onSubmitHandle = async (sub) => {
    await onSubmitRef.current(sub);
  };

  const GET_CERTIFICATE_DATA = (issuer_credential_template_id) => {
    GET(
      EntityConstants.GET_DRAFT_CERTIFICATE_DATA +
        issuer_credential_template_id,
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: t("drafts:error"),
            message: data.error,
          });
        } else {
          setCertificateData(data);
          setFormData(data?.template_variables_data?.data || {});
          prefillSnapshotRef.current = null;
          setUploadedThumbnailImage(data?.thumbnail_image);

          // Set the initial logo from template data
          const initialLogo =
            data?.template_variables_data?.data?.logo?.[0]?.url;
          if (initialLogo) {
            setOrgLogo(initialLogo);
            setFieldsChanged((prev) => ({
              ...prev,
              logo: data.template_variables_data.data.logo,
            }));
          }

          // Initialize selectedComponents from draft data
          if (data.verification_fields?.components) {
            setSelectedComponents(data.verification_fields.components);
          }

          const expiryState = getExpiryStateFromData(data);
          setIsExpiryRequired(expiryState.isExpiryRequired);
          setExpiryValue(expiryState.expiryValue);
          setExpiryUnit(expiryState.expiryUnit);

          const isNewTemplateAddition =
            localStorage.getItem("is_new_template_addition") === "true";
          if (!isNewTemplateAddition && data.batch) {
            setBatchName(data.batch.batch_name);
            localStorage.setItem("batch_code", data.batch.batch_code);
          }
        }
      });
  };

  const formFieldsChanged = (fields) => {
    if (fields?.data) {
      setFormData(fields.data);
      setFieldsChanged(fields.data);

      // Update orgLogo state if a new logo is uploaded
      if (fields.data.logo && fields.data.logo[0]?.url) {
        setOrgLogo(fields.data.logo[0].url);
      }
    }

    if (fields.changed?.component?.key === "logo") {
      const newLogo = fields.changed?.value;
      if (newLogo && newLogo[0]?.url) {
        setFieldsChanged((prev) => ({
          ...prev,
          logo: newLogo,
        }));
        setOrgLogo(newLogo[0].url);
      }
    } else if (
      fields.changed?.component?.key !== undefined &&
      fields.changed?.value !== undefined
    ) {
      if (
        fields.changed?.value[0]?.type &&
        imgRegex.test(fields.changed?.value[0].url)
      ) {
        setFieldsChanged((prevData) => ({
          ...prevData,
          // Add your new key-value pair here
          [fields.changed?.component?.key]: fields.changed?.value[0].url,
        }));
      } else {
        setFieldsChanged((prevData) => ({
          ...prevData,
          // Add your new key-value pair here
          [fields.changed?.component?.key]: fields.changed?.value,
        }));
      }
    } else if (fields?.data) {
      setFieldsChanged(fields.data);
    }
  };

  const applyFormValues = (values) => {
    setFormData(values);
    setFieldsChanged(values);
    if (values.logo?.[0]?.url) setOrgLogo(values.logo[0].url);
    const form = formRef.current;
    if (form?.element && document.body.contains(form.element)) {
      form.submission = { data: { ...values } };
    }
  };

  const handlePrefillToggle = (next) => {
    const turnOn = typeof next === "function" ? next(isToggleOn) : next;
    if (turnOn === isToggleOn) return;
    setIsToggleOn(turnOn);
    if (turnOn) {
      prefillSnapshotRef.current = formData;
      const defaults = Object.fromEntries(
        Object.entries(defaultFormValues).filter(
          ([key, value]) =>
            key !== "submit" &&
            value !== undefined &&
            value !== null &&
            value !== "" &&
            !(Array.isArray(value) && value.length === 0),
        ),
      );
      applyFormValues({ ...formData, ...defaults });
    } else {
      applyFormValues(
        prefillSnapshotRef.current ||
          certificateData?.template_variables_data?.data ||
          {},
      );
      prefillSnapshotRef.current = null;
    }
  };

  useEffect(() => {
    preAuthUserEditedRef.current = false;
    issuer_credential_template_id &&
      GET_CERTIFICATE_DATA(issuer_credential_template_id);
  }, [issuer_credential_template_id]);

  const fetchSvg = async (certificateData) => {
    const { template_variables_data, html_url } = certificateData;
    const { url, templateType } = resolveTemplateSource(certificateData);
    const isHtml = isHtmlTemplate(templateType);
    try {
      const response = await fetch(`${isHtml ? html_url || url : url}`);
      if (response.ok) {
        const svgContent = await response.text();
        const isHtmlContent = isHtmlTemplateSource(svgContent, templateType);
        const svgWithStableIds = isHtmlContent
          ? svgContent
          : applyStableSvgVariableElementIds(svgContent);
        setPreviewTemplateType(templateType);
        setCurrentSVG(svgWithStableIds);
        setFieldsChanged((current) => ({
          ...(template_variables_data?.data || {}),
          ...current,
        }));
        //!injecting the svg content into the container
        const container = document.getElementById("certificate_container");
        container.style.filter = "drop-shadow(0 0 2px rgba(0, 0, 0, 0.264))";
        injectTemplatePreview(container, svgWithStableIds, templateType);
      } else if (response.status === 404) {
        const container = document.getElementById("certificate_container");
        renderToContainer(
          container,
          <div className="text-black flex justify-center w-full h-full text-lg">
            <div className="flex flex-col items-center gap-2 w-fit h-fit">
              <span className="flex items-center gap-2 w-fit h-fit  bg-gray-100 px-2 py-1 rounded-lg">
                <p>{t("drafts:couldnt_find_image")}</p>
                <LuImageOff className="text-cfPrimaryCardHover " />
              </span>
              <span>
                <p>{t("drafts:create_template_again")}</p>
              </span>
            </div>
          </div>,
        );
      } else {
        const container = document.getElementById("certificate_container");
        renderToContainer(
          container,
          <div className="text-black flex justify-center w-full h-full text-lg">
            <button
              type="button"
              className="flex items-center gap-2 w-fit h-fit rounded-lg cursor-pointer focus:outline-none"
              onClick={() => {
                renderToContainer(
                  container,
                  <span className="w-full flex justify-center">
                    <CfLoader />
                  </span>,
                );
                certificateData && fetchSvg(certificateData);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  renderToContainer(
                    container,
                    <span className="w-full flex justify-center">
                      <CfLoader />
                    </span>,
                  );
                  certificateData && fetchSvg(certificateData);
                }
              }}
            >
              <p>{t("drafts:couldnt_load_image")}</p>
              <RxReload className="text-cfPrimaryCardHover " />
            </button>
          </div>,
        );
      }
    } catch (error) {
      console.error("Error loading SVG:", error);
    }
  };

  const handleCheckboxChange = (componentKey) => {
    setSelectedComponents((prevComponents) =>
      prevComponents.map((comp) =>
        comp.key === componentKey
          ? { ...comp, selected: !comp.selected }
          : comp,
      ),
    );
  };

  const GET_TEMPLATE_FORM_DATA = async (credential_template_id) => {
    try {
      const certificateResponse = await GET(
        `${EntityConstants.GET_CREDENTIALS_TEMPLATE_DESIGN}${credential_template_id}/design`,
      );
      const certificateData = await certificateResponse.json();
      const defaults = certificateData?.default_template_variables_data;
      setDefaultFormValues(
        (defaults?.data && typeof defaults.data === "object"
          ? defaults.data
          : defaults) || {},
      );

      if (certificateData?.form_io_url) {
        const formComponentsResponse = await fetch(certificateData.form_io_url);
        const formComponents = await formComponentsResponse.json();
        const hasFields = hasFormDataAttributes(formComponents);

        setHasFormFields(hasFields);
      } else {
        setHasFormFields(false);
      }
    } catch (error) {
      console.error("Error loading template form data:", error);
      setHasFormFields(false);
    }
  };

  useEffect(() => {
    GET_TEMPLATE_FORM_DATA(credential_template_id);
  }, [credential_template_id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        programmeDropdownRef.current &&
        !programmeDropdownRef.current.contains(event.target)
      ) {
        setProgrammeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        batchDropdownRef.current &&
        !batchDropdownRef.current.contains(event.target)
      ) {
        setBatchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchProgrammes();
  }, []);

  useEffect(() => {
    const isNewTemplateAddition =
      localStorage.getItem("is_new_template_addition") === "true";
    if (isNewTemplateAddition) {
      const storedProgrammeName = localStorage.getItem("programme_name");
      const storedProgrammeCode = localStorage.getItem("programme_code");
      const storedBatchName = localStorage.getItem("batch_name");

      if (storedProgrammeName) {
        setProgrammeName(storedProgrammeName);
      }
      if (storedProgrammeCode) {
        selectedProgrammeCodeRef.current = storedProgrammeCode;
        setSelectedProgrammeCode(storedProgrammeCode);
      }
      if (storedBatchName) {
        setBatchName(storedBatchName);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedProgrammeCode) {
      fetchBatches();
    }
  }, [selectedProgrammeCode]);

  useEffect(() => {
    if (!certificateData || programmeName || selectedProgrammeCode) return;
    if (programmeList.length !== 1) return;

    const isNewTemplateAddition =
      localStorage.getItem("is_new_template_addition") === "true";
    if (
      !isNewTemplateAddition &&
      certificateData?.programme?.programme_code
    ) {
      return;
    }

    handleProgrammeSelect(programmeList[0]);
  }, [certificateData, programmeList, programmeName, selectedProgrammeCode]);

  useEffect(() => {
    if (!selectedProgrammeCode || batchName || batchList.length !== 1) return;

    const isNewTemplateAddition =
      localStorage.getItem("is_new_template_addition") === "true";
    const savedBatchMatchesProgramme =
      !isNewTemplateAddition &&
      certificateData?.batch?.batch_code &&
      certificateData?.programme?.programme_code === selectedProgrammeCode;
    if (savedBatchMatchesProgramme) return;

    handleBatchSelect(batchList[0]);
  }, [
    batchList,
    batchName,
    selectedProgrammeCode,
    certificateData,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("verCost");
      localStorage.removeItem("verCheck");
      localStorage.removeItem("programme_code");
      localStorage.removeItem("programme_name");
      localStorage.removeItem("batch_code");
      localStorage.removeItem("batch_name");
      localStorage.removeItem("is_new_template_addition");
      localStorage.removeItem("issuer_template_id");
      sessionStorage.removeItem("thumbnail_image");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      localStorage.removeItem("verCost");
      localStorage.removeItem("verCheck");
      localStorage.removeItem("programme_code");
      localStorage.removeItem("programme_name");
      localStorage.removeItem("batch_code");
      localStorage.removeItem("batch_name");
      localStorage.removeItem("is_new_template_addition");
      localStorage.removeItem("issuer_template_id");
      sessionStorage.removeItem("thumbnail_image");
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (
      certificateData instanceof Object &&
      Object.prototype.hasOwnProperty.call(
        certificateData,
        "template_variables_data",
      )
    ) {
      fetchSvg(certificateData);
    }
    certificateData?.certificate_name &&
      setCertificateTitle(certificateData?.certificate_name);

    if (certificateData?.thumbnail_image) {
      setThumbnailImagePreview(certificateData.thumbnail_image);
    }

    const isNewTemplateAddition =
      localStorage.getItem("is_new_template_addition") === "true";
    if (!isNewTemplateAddition) {
      certificateData?.programme?.programme_name &&
        setProgrammeName(certificateData?.programme?.programme_name);
      if (certificateData?.programme?.programme_code) {
        selectedProgrammeCodeRef.current =
          certificateData?.programme?.programme_code;
        setSelectedProgrammeCode(certificateData?.programme?.programme_code);
      }
      certificateData?.programme?.programme_code &&
        localStorage.setItem(
          "programme_code",
          certificateData?.programme?.programme_code,
        );
    }

    certificateData?.certificate_redirect_url &&
      setCertificateRedirectURL(certificateData?.certificate_redirect_url);
    certificateData?.is_auth_required &&
      setIsAuthRequired(certificateData?.is_auth_required);
    certificateData?.is_public && setIsPublic(certificateData?.is_public);
    if (
      !preAuthUserEditedRef.current &&
      certificateData?.is_pre_auth !== undefined
    ) {
      setIsPreAuth(certificateData?.is_pre_auth === true);
    }
    certificateData?.is_payment_required &&
      setIsPaymentRequired(certificateData?.is_payment_required);
    if (certificateData?.is_payment_required) {
      localStorage.setItem("verCheck", "true");
    }
    certificateData?.verification_cost &&
      setVerificationCost(certificateData?.verification_cost / 100);
    if (certificateData?.verification_cost) {
      localStorage.setItem("verCost", certificateData?.verification_cost / 100);
    }
  }, [certificateData]);

  useEffect(() => {
    if (currentSVG) {
      const updatedFields = {
        ...fieldsChanged,
        logo: fieldsChanged.logo?.[0]?.url || orgLogo || "",
      };

      updateTemplatePreview(
        document.getElementById("certificate_container"),
        currentSVG,
        updatedFields,
        previewTemplateType,
      );
    }
  }, [fieldsChanged, currentSVG, previewTemplateType, orgLogo]);

  useEffect(() => {
    if (
      certificateTitle.length === 0 ||
      whiteSpaceEmptyStrRegex.test(certificateTitle)
    ) {
      setError("Certificate Name is required");
      sessionStorage.removeItem("certificate_title");
    } else {
      sessionStorage.setItem("certificate_title", certificateTitle);
      setError("");
    }
  }, [certificateTitle]);

  useEffect(() => {
    if (programmeName.trim().length === 0) {
      setProgrammeNameError("Programme Name is required");
      sessionStorage.removeItem("programme_name");
    } else {
      sessionStorage.setItem("programme_name", programmeName);
      setProgrammeNameError("");
    }
  }, [programmeName]);

  useEffect(() => {
    if (batchName.trim().length === 0) {
      setBatchNameError("Batch Name is required");
      sessionStorage.removeItem("batch_name");
    } else {
      sessionStorage.setItem("batch_name", batchName);
      setBatchNameError("");
    }
  }, [batchName]);

  useEffect(() => {
    if (
      certificateRedirectURL.length === 0 ||
      whiteSpaceEmptyStrRegex.test(certificateRedirectURL)
    ) {
      sessionStorage.removeItem("certificate_redirect_url");
    } else {
      sessionStorage.setItem(
        "certificate_redirect_url",
        certificateRedirectURL,
      );
    }
  }, [certificateRedirectURL]);

  useEffect(() => {
    sessionStorage.setItem("is_auth_required", isAuthRequired);
  }, [isAuthRequired]);

  useEffect(() => {
    sessionStorage.setItem("is_public", isPublic);
  }, [isPublic]);

  useEffect(() => {
    sessionStorage.setItem("is_pre_auth", isPreAuth);
  }, [isPreAuth]);

  useEffect(() => {
    sessionStorage.setItem("is_payment_required", isPaymentRequired);
  }, [isPaymentRequired]);

  useEffect(() => {
    sessionStorage.setItem("verification_cost", verificationCost);
  }, [verificationCost]);

  useEffect(() => {
    const formContainer = document.getElementById("formio");
    if (!formContainer || !certificateData?.form_io_url) return undefined;

    let cancelled = false;
    let createdForm = null;
    formContainer.innerHTML = "";
    fetch(certificateData.form_io_url)
      .then((resp) => resp.json())
      .then((formSchema) => {
        if (cancelled) return undefined;
        return Formio.createForm(formContainer, formSchema, {}).then((form) => {
          createdForm = form;
          if (cancelled) {
            form.destroy();
            return;
          }
          formRef.current = form;
          form.submission = { data: { ...formData } };
          form.on("change", formFieldsChanged);
          form.on("submit", (sub) => onSubmitHandle(sub));
          sessionStorage.removeItem("issuer_credential_template_id");
        });
      })
      .catch((error) => console.error("Error loading form:", error));

    return () => {
      cancelled = true;
      if (createdForm) {
        createdForm.destroy();
        if (formRef.current === createdForm) formRef.current = null;
      }
    };
  }, [certificateData?.form_io_url, activeTab]);

  const handleAuthRequiredChange = (e) => {
    const newAuthRequired = e.target.checked;
    if (newAuthRequired && isPublic) {
      Toast({
        type: "error",
        title: t("drafts:invalid_combination"),
        message: t("drafts:auth_public_conflict_error"),
      });
      return;
    }
    setIsAuthRequired(newAuthRequired);
  };

  const handleIsPublicChange = (e) => {
    const newIsPublic = e.target.checked;
    if (newIsPublic && isAuthRequired) {
      Toast({
        type: "error",
        title: t("drafts:invalid_combination"),
        message: t("drafts:public_auth_conflict_error"),
      });
      return;
    }
    setIsPublic(newIsPublic);
  };

  const handlePreAuthChange = (e) => {
    preAuthUserEditedRef.current = true;
    setIsPreAuth(e.target.checked);
  };

  useEffect(() => {
    const validateToggleCombinations = () => {
      const hasAuth = isAuthRequired;
      const isPub = isPublic;
      const hasPayment = isPaymentRequired;

      const allDisabled = !hasPayment && !hasAuth && !isPub;
      if (allDisabled) {
        setIsInvalidCombination(false);
        setToggleMessage("");
        return;
      }

      if (hasAuth && isPub) {
        setIsInvalidCombination(true);
        setToggleMessage(`${t("drafts:invalid_combination_desc")}`);
        return;
      }
      setIsInvalidCombination(false);

      const handleVerifyPortalClick = () =>
        window.open("https://verify.credissuer.com/", "_blank");
      const handleMyCertsClick = () =>
        window.open("https://mycerts.credissuer.com/", "_blank");

      if (hasPayment && hasAuth && isPub) {
        setToggleMessage(
          <span>• {t("drafts:only_paid_verification_apply")}</span>,
        );
      } else if (hasPayment && hasAuth) {
        setToggleMessage(
          <span>
            • {t("drafts:pay_fee_msg_start")}{" "}
            <button
              className="font-bold text-cfPrimaryButton underline focus:outline-none rounded"
              onClick={handleVerifyPortalClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVerifyPortalClick();
                }
              }}
              tabIndex={0}
            >
              {t("drafts:verify_portal")}
            </button>
            .<br />• {t("drafts:cert_not_shared_msg")}{" "}
            <button
              className="font-bold text-cfPrimaryButton underline focus:outline-none rounded"
              onClick={handleMyCertsClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleMyCertsClick();
                }
              }}
              tabIndex={0}
            >
              {t("drafts:my_certs")}
            </button>{" "}
            {t("drafts:complete_auth_download")}
          </span>,
        );
      } else if (hasPayment && isPub) {
        setToggleMessage(
          <span>
            • {t("drafts:cert_sent_email_notification")}
            <br />• {t("drafts:during_verification_pay_fee")}{" "}
            <button
              className="font-bold text-cfPrimaryButton underline focus:outline-none rounded"
              onClick={handleVerifyPortalClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVerifyPortalClick();
                }
              }}
              tabIndex={0}
            >
              {t("drafts:verify_portal")}
            </button>
            .
          </span>,
        );
      } else if (hasAuth && isPub) {
        setToggleMessage(<span>• {t("drafts:invalid_combination")}</span>);
      } else if (hasPayment) {
        setToggleMessage(
          <span>
            • {t("drafts:paid_verification_hint")}{" "}
            <button
              className="font-bold text-cfPrimaryButton underline focus:outline-none rounded"
              onClick={handleVerifyPortalClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleVerifyPortalClick();
                }
              }}
              tabIndex={0}
            >
              {t("drafts:verify_portal")}
            </button>{" "}
            {t("drafts:for_verification")}
          </span>,
        );
      } else if (hasAuth) {
        setToggleMessage(
          <span>
            • {t("drafts:auth_required_hint")}{" "}
            <button
              className="font-bold text-cfPrimaryButton underline focus:outline-none rounded"
              onClick={handleMyCertsClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleMyCertsClick();
                }
              }}
              tabIndex={0}
            >
              {t("drafts:my_certs")}
            </button>
            .
          </span>,
        );
      } else if (isPub) {
        setToggleMessage(<span>• {t("drafts:is_public_hint")}</span>);
      }
    };

    validateToggleCombinations();
  }, [isPaymentRequired, isAuthRequired, isPublic]);

  return (
    <div className="mt-20" id={pageRootTestIds.editIssuerTemplate}>
      {breadcrumbItems.length > 0 && (
        <CfBreadcrumb
          breadcrumbItems={breadcrumbItems}
          isToggleOn={isToggleOn}
          setIsToggleOn={handlePrefillToggle}
          hasFormFields={hasFormFields}
          testId="edit-issuer-template-breadcrumb"
        />
      )}
      <div className="flex flex-col md:flex-row w-full">
        <div className="w-full md:w-[30%] md:shrink-0 max-h-none md:max-h-[84vh] md:min-h-[84vh] overflow-y-auto flex flex-col items-center bg-cfSecondary min-h-full">
          <div className="flex space-x-0 md:space-x-4 mb-2 mt-4 bg-gray-300 rounded-xl md:rounded-2xl p-1 mx-1 md:mx-0">
            <button
              className={`px-2 md:px-3 py-1 transition-all duration-200 ${
                activeTab === "basic"
                  ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                  : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
              }`}
              onClick={() => handleTabChange("basic")}
            >
              {t("drafts:basic_settings")}
            </button>
            <button
              id="edit-issuer-template-advanced-settings-tab"
              className={`px-1 md:px-3 py-1 transition-all duration-200 ${
                activeTab === "advanced"
                  ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                  : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
              }`}
              onClick={() => handleTabChange("advanced")}
            >
              {t("drafts:advanced_settings")}
            </button>
          </div>

          <div className="w-full px-4 py-2">
            {activeTab === "basic" && (
              <div className="flex flex-col w-full items-start">
                <label
                  htmlFor="certificateName"
                  className="after:content-['*'] after:text-red-600"
                >
                  {t("drafts:certificate_name")}
                </label>
                <div className="flex justify-between items-center focus-within:border-4 focus-within:border-cfCertificateNameInputBorder rounded-xl w-full">
                  <div className="flex justify-between items-center border-[1px] rounded-md p-1 w-full">
                    <input
                      type="text"
                      name="certificateName"
                      className="border-none rounded-md w-full focus:outline-none"
                      id="certificateName"
                      onChange={(e) => setCertificateTitle(e.target.value)}
                      value={certificateTitle}
                    />
                  </div>
                </div>
                {error && <p className="text-red-600">{error}</p>}
                <label
                  htmlFor="programmeName"
                  className="mt-4 after:content-['*'] after:text-red-600"
                >
                  {t("drafts:programme_name")}
                </label>
                <div className="relative w-full" ref={programmeDropdownRef}>
                  <button
                    type="button"
                    id="edit-issuer-template-programme-dropdown-trigger"
                    className="flex justify-between items-center border-[1px] rounded-md p-1 w-full cursor-pointer focus:outline-none"
                    onClick={() => {
                      setProgrammeDropdownOpen(!programmeDropdownOpen);
                      if (
                        !programmeDropdownOpen &&
                        programmeList.length === 0
                      ) {
                        fetchProgrammes();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setProgrammeDropdownOpen(!programmeDropdownOpen);
                        if (
                          !programmeDropdownOpen &&
                          programmeList.length === 0
                        ) {
                          fetchProgrammes();
                        }
                      }
                    }}
                  >
                    <input
                      type="text"
                      name="programmeName"
                      className="border-none rounded-md w-full focus:outline-none cursor-pointer"
                      id="programmeName"
                      value={programmeName}
                      readOnly
                      placeholder={t("drafts:select_programme")}
                    />
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        programmeDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {programmeDropdownOpen && (
                    <div
                      className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleProgrammeScroll}
                    >
                      {programmeList.length > 0 ? (
                        programmeList.map((programme) => (
                          <div
                            key={programme.programme_code}
                            id={`edit-issuer-template-programme-option-${programme.programme_code}`}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleProgrammeSelect(programme);
                              setProgrammeName(programme.programme_name);
                              setProgrammeDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleProgrammeSelect(programme);
                                setProgrammeName(programme.programme_name);
                                setProgrammeDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {programme.programme_name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {programmeLoading
                            ? t("drafts:loading")
                            : t("drafts:no_programmes_found")}
                        </div>
                      )}
                      {programmeLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          <CfLoader size="small" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {programmeNameError && (
                  <p className="text-red-600">{programmeNameError}</p>
                )}

                <label
                  htmlFor="batchName"
                  className="mt-4 after:content-['*'] after:text-red-600"
                >
                  {t("drafts:batch_name")}
                </label>
                <div className="relative w-full" ref={batchDropdownRef}>
                  <button
                    type="button"
                    id="edit-issuer-template-batch-dropdown-trigger"
                    className="flex justify-between items-center border-[1px] rounded-md p-1 w-full cursor-pointer focus:outline-none"
                    onClick={() => {
                      if (!selectedProgrammeCode) {
                        Toast({
                          type: "warning",
                          title: t("drafts:warning"),
                          message: t("drafts:select_programme_first"),
                        });
                        return;
                      }
                      setBatchDropdownOpen(!batchDropdownOpen);
                      if (!batchDropdownOpen && batchList.length === 0) {
                        fetchBatches();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        if (!selectedProgrammeCode) {
                          Toast({
                            type: "warning",
                            title: t("drafts:warning"),
                            message: t("drafts:select_programme_first"),
                          });
                          return;
                        }
                        setBatchDropdownOpen(!batchDropdownOpen);
                        if (!batchDropdownOpen && batchList.length === 0) {
                          fetchBatches();
                        }
                      }
                    }}
                    style={{
                      backgroundColor: !selectedProgrammeCode
                        ? "#f3f4f6"
                        : "inherit",
                      cursor: !selectedProgrammeCode
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <input
                      type="text"
                      name="batchName"
                      className="border-none rounded-md w-full focus:outline-none cursor-pointer"
                      id="batchName"
                      value={batchName}
                      readOnly
                      placeholder={
                        !selectedProgrammeCode
                          ? t("drafts:select_programme_first")
                          : t("drafts:select_batch")
                      }
                    />
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        batchDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {batchDropdownOpen && (
                    <div
                      className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleBatchScroll}
                    >
                      {batchList.length > 0 ? (
                        batchList.map((batch) => (
                          <div
                            key={batch.batch_code}
                            id={`edit-issuer-template-batch-option-${batch.batch_code}`}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleBatchSelect(batch);
                              setBatchName(batch.batch_name);
                              setBatchDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleBatchSelect(batch);
                                setBatchName(batch.batch_name);
                                setBatchDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {batch.batch_name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {batchLoading
                            ? t("drafts:loading")
                            : t("drafts:no_batches_found")}
                        </div>
                      )}
                      {batchLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          <CfLoader size="small" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {batchNameError && (
                  <p className="text-red-600">{batchNameError}</p>
                )}

                <label htmlFor="thumbnailImage" className="mt-4">
                  {t("drafts:thumbnail_image_optional")}
                </label>
                <div className="text-gray-600 text-sm mb-1">
                  {t("drafts:upload_thumbnail_hint")}
                </div>
                <div className="flex justify-between items-center border-[1px] rounded-md p-1 w-full">
                  <input
                    type="file"
                    accept=".png,.jpeg,.jpg,image/png,image/jpeg,image/jpg"
                    onChange={handleThumbnailImageUpload}
                    className="w-full cursor-pointer text-sm text-gray-500
                      file:mr-4 file:py-1.5 file:px-3
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-indigo-50 file:text-indigo-700
                      hover:file:bg-indigo-100
                      focus:outline-none"
                    id="thumbnailImage"
                    ref={thumbnailImageInputRef}
                  />
                </div>
                {thumbnailImagePreview && (
                  <div className="relative border rounded-md p-2 mt-2 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">
                        {t("drafts:preview")}:
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveThumbnailImage}
                        className="text-red-600 hover:text-red-800 text-sm font-medium focus:outline-none"
                      >
                        {t("drafts:remove")}
                      </button>
                    </div>
                    <img
                      src={thumbnailImagePreview}
                      alt={t("drafts:thumbnail_preview")}
                      className="max-w-full h-auto max-h-48 rounded-md mx-auto"
                    />
                  </div>
                )}

                <div id="formio" className="w-full py-2 h-full">
                  <div className="flex justify-center items-center">
                    <CfLoader />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "advanced" && (
              <div className="advanced-settings">
                <div className="flex flex-col mb-4 mt-2">
                  <div
                    htmlFor="verification-fields"
                    className="text-gray-800 font-semibold"
                  >
                    {t("drafts:delivery_verification_settings")}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {t("drafts:delivery_settings_desc")}
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <SettingOption
                    key={"toggle_is_payment_required"}
                    toggleStatus={isPaymentRequired}
                    heading={t("drafts:enable_paid_verification")}
                    onclickFunction={handlePaymentCheckboxChange}
                    label={t("drafts:enable_paid_verification")}
                  />
                </div>
                {isPaymentRequired && (
                  <div className="mb-4">
                    <label
                      htmlFor="verification_amount"
                      className="text-gray-600"
                    >
                      {t("drafts:verification_amount_label")}
                    </label>
                    <div className="flex items-center rounded-md bg-white pl-3 outline outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-indigo-600">
                      <div className="shrink-0 select-none text-base text-gray-500 sm:text-sm/6 mr-2.5">
                        ₹
                      </div>
                      <input
                        type="number"
                        name="price"
                        id="price"
                        min="0.01"
                        className="block min-w-0 grow py-1.5 pl-1 pr-3 text-base text-gray-800 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6 border border-gray-500"
                        placeholder="0.00"
                        value={verificationCost}
                        onChange={handleVerificationCostChange}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    {!verificationCost && (
                      <p className="text-red-600 mt-1">
                        {t("drafts:verification_amount_required_error")}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <SettingOption
                    key={"toggle_is_expiry_required"}
                    toggleStatus={isExpiryRequired}
                    heading={t("drafts:is_expiry_required")}
                    onclickFunction={handleExpiryRequiredChange}
                    label={t("drafts:is_expiry_required")}
                  />
                </div>

                {isExpiryRequired && (
                  <div className="mb-4">
                    <label htmlFor="expiry_duration" className="text-gray-600">
                      {t("drafts:expiry_duration_label")}
                    </label>
                    <div className="mt-1 flex items-center rounded-md bg-white pl-3 pr-3 outline outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-indigo-600 has-[select:focus]:outline has-[select:focus]:outline-2 has-[select:focus]:-outline-offset-2 has-[select:focus]:outline-indigo-600">
                      <div className="flex-1">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          id="expiry_duration"
                          name="expiry_duration"
                          className="block w-full min-w-0 py-1.5 pl-1 pr-3 text-base text-gray-800 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6 border-none bg-transparent"
                          placeholder={t("drafts:enter_expiry_value")}
                          value={expiryValue}
                          onChange={handleExpiryValueChange}
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                      <div className="h-6 w-px bg-gray-300 mx-2 shrink-0" />
                      <div className="w-[112px] shrink-0">
                        <Dropdown
                          options={expiryUnitOptions}
                          selected={
                            expiryUnitOptions.find(
                              (option) => option.value === expiryUnit,
                            ) || expiryUnitOptions[0]
                          }
                          onSelect={handleExpiryUnitChange}
                          testId="edit-issuer-template-dropdown-expiry-unit"
                          buttonClass="border-0 bg-transparent px-2 py-1.5 text-sm text-gray-800 shadow-none"
                          menuClass="right-0 left-auto mt-1 min-w-[120px]"
                        />
                      </div>
                    </div>
                    {(!expiryValue ||
                      calculateExpiryDays(expiryValue, expiryUnit) <= 0) && (
                      <p className="text-red-600 mt-1">
                        {t("drafts:expiry_date_required_error")}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <SettingOption
                    key={"toggle_is_auth_required"}
                    toggleStatus={isAuthRequired}
                    heading={t("drafts:is_authentication_required")}
                    onclickFunction={handleAuthRequiredChange}
                    label={t("drafts:is_authentication_required")}
                    disabled={isPublic}
                  />
                </div>

                <div>
                  <SettingOption
                    key={"toggle_is_public"}
                    toggleStatus={isPublic}
                    heading={t("drafts:is_public")}
                    onclickFunction={handleIsPublicChange}
                    label={t("drafts:is_public")}
                    disabled={isAuthRequired}
                  />
                </div>

                <div>
                  <SettingOption
                    key={"toggle_is_pre_auth"}
                    toggleStatus={isPreAuth}
                    heading={t("drafts:is_pre_auth_required")}
                    onclickFunction={handlePreAuthChange}
                    label={t("drafts:is_pre_auth_required")}
                    testId="toggle-is-pre-auth"
                  />
                </div>

                {(toggleMessage || isPreAuth) && (
                  <div
                    className={`mb-4 p-4 rounded-lg border-none ${
                      isInvalidCombination
                        ? "bg-red-50 border-red-200 text-red-800"
                        : "bg-blue-50 border-blue-200 text-cfPrimaryButton"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">
                      {t("drafts:current_settings")}:
                    </h4>
                    <div className="text-sm space-y-2">
                      {toggleMessage &&
                        (typeof toggleMessage === "string" ? (
                          <div className="whitespace-pre-line">
                            {toggleMessage}
                          </div>
                        ) : (
                          <div>{toggleMessage}</div>
                        ))}
                      {isPreAuth && (
                        <div>• {t("drafts:pre_auth_enabled_hint")}</div>
                      )}
                    </div>
                  </div>
                )}

                {selectedComponents.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-col mb-4">
                      <div
                        htmlFor="verification-fields"
                        className="text-gray-800 font-semibold"
                      >
                        {t("drafts:verification_fields")}
                      </div>
                      <div className="text-gray-600 text-sm">
                        {t("drafts:verification_fields_desc")}
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded p-2">
                      {selectedComponents.map((component) => (
                        <div
                          key={component.key}
                          className="flex items-center mb-2"
                        >
                          <input
                            type="checkbox"
                            checked={component.selected}
                            onChange={() => handleCheckboxChange(component.key)}
                            className="mr-2 rounded-lg mb-2"
                            id={`checkbox-${component.key}`}
                          />
                          <label htmlFor={`checkbox-${component.key}`}>
                            {component.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  id="edit-issuer-template-submit"
                  className="submit-btn mt-6 px-4 py-2 bg-cfPrimaryButton text-cfSecondary w-full text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={handleSubmit}
                  disabled={loading || isInvalidCombination}
                >
                  {isInvalidCombination
                    ? t("drafts:fix_invalid_settings")
                    : t("drafts:submit")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="w-full min-w-0 flex-1 p-3">
          <div className="w-full">
            <div
              className="flex flex-col flex-shrink max-h-[78vh]"
              id="certificate_container"
            >
              <CfLoader />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditIssuerTemplate;
